import type { ReactNode } from "react";
import { createRef, useEffect, useState } from "react";
import Icon from "../Icon";
import { BaseProps } from "./items/BaseProps";
import { BatchClassMetadataData } from "../../models/config/BatchClassMetadata";
import { setRef } from "../../utils/SetRef";
import { ExpandingTooltipReturn } from "../hooks/useAdvancedTooltip";
import { ClearableInput } from "../inputs/ClearableInput";
import { isDescendant } from "./ActionBarCategory";
import "../../styles/actionbar/category.css";

export { FilterableActionBarCategory };

type Props<T> = {
  list: T[];
  order?: number;
  title: string;
  stretch?: boolean;
  /**
   * If set, all children are marked as disabled
   */
  disableChildren?: boolean;
  children: (object: T, childProps: {}) => ReactNode;
  /**
   * Indicates that a child item is selected
   */
  underlined?: boolean;
  tProps?: ExpandingTooltipReturn["triggerProps"];
};
const FilterableActionBarCategory = (props: Props<BatchClassMetadataData>) => {
  const categoryRootRef = createRef<HTMLDivElement>();
  const [open, setOpen] = useState(false);
  const [openWidth, setOpenWidth] = useState<number>(-1);
  const [filter, setFilter] = useState("");

  let className = "actionBarCategory ";
  if (props.stretch) {
    className += "stretcher ";
  }
  if (props.underlined) {
    className += "underlined ";
  }
  if (props.disableChildren) {
    className += "disabled ";
  }

  let newChildProps: Partial<BaseProps> = {
    subCategory: true,
  };
  if (props.disableChildren) {
    newChildProps = { ...newChildProps, disabled: true };
  }
  return (
    <div
      {...props.tProps}
      ref={(r) => {
        setRef(categoryRootRef, r);
        if (props.tProps) {
          setRef(props.tProps.ref, r);
        }
      }}
      className={className}
      onMouseEnter={(e) => {
        // Do not open if no elements are in the list
        if (props.list.length < 2) return;
        // clamp width of input to clientWidth of span, or ref, or auto
        setOpenWidth(
          e.currentTarget.querySelector("span[data-abi-title]")?.parentElement
            ?.clientWidth ??
            categoryRootRef.current?.clientWidth ??
            -1,
        );
        setOpen(true);
        if (props.tProps && props.tProps.onMouseEnter)
          props.tProps.onMouseEnter();
      }}
      onMouseLeave={(e) => {
        if (!isDescendant(e.currentTarget, e.relatedTarget as HTMLElement)) {
          setOpen(false);
        }
        if (props.tProps && props.tProps.onMouseLeave)
          props.tProps.onMouseLeave();
      }}
    >
      <div
        className={"actionBarCategoryText"}
        style={
          {
            // force text to be width of span[data-abi-title] when filtering available
            // maxWidth: open && openWidth > -1 ? `${openWidth}px` : undefined,
          }
        }
      >
        {!open && (
          <>
            <span data-abi-title>{props.title}</span>
            <Icon altText={""} icon={"chevron-down"} />
          </>
        )}
        {/* Show chevron when list is closed  */}
        {open && props.list.length > 2 && (
          <ClearableInput
            value={filter}
            placeholder={props.title}
            onClear={() => setFilter("")}
            onChange={(e) => {
              setFilter(e.target.value);
            }}
            style={{
              width:
                open && openWidth && openWidth > -1
                  ? `${openWidth}px`
                  : undefined,
              padding: 0,
              // width:
            }}
          />
        )}
      </div>
      <ul
        className={`items ${open ? "open" : ""}`}
        style={{
          minWidth:
            (categoryRootRef.current &&
            categoryRootRef.current?.clientWidth > -1
              ? `${categoryRootRef.current.clientWidth}px`
              : undefined) ?? "inherit",
        }}
      >
        {props.list
          .filter((x) => {
            if (filter == "") {
              return true;
            }
            return (
              x.batchClassId.toLowerCase().startsWith(filter.toLowerCase()) ||
              x.name.toLowerCase().includes(filter.toLowerCase())
            );
          })
          .map((x) => props.children(x, newChildProps))}
      </ul>
    </div>
  );
};
